/* eslint-disable react/no-array-index-key */
/* eslint-disable array-callback-return */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getDataView } from "@insite/client-framework/Store/Data/DataState";
import { loadPagesForParent } from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/NewsList/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import PageProps from "@insite/client-framework/Types/PageProps";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Pagination, { PaginationPresentationProps } from "@insite/mobius/Pagination";
import Select from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import convertToNumberIfString from "@insite/mobius/utilities/convertToNumberIfString";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import sortBy from "lodash/sortBy";
import truncate from "lodash/truncate";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    defaultPageSize = "defaultPageSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultPageSize]: number;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: WidgetProps) => {
    const pages = getDataView(state.data.pages, { parentNodeId: getCurrentPage(state).nodeId });
    const blogPageURL = state.data.pages.location.pathname;
    let links: SafeDictionary<PageLinkModel> = {};
    if (pages.value) {
        links = pages.value.reduce(
            (links, page) => ({ ...links, [page.nodeId]: getPageLinkByNodeId(state, page.nodeId) }),
            {},
        );
    }
    return {
        parentNodeId: getCurrentPage(state).nodeId,
        location: getLocation(state),
        blogPageURL,
        pages,
        links,
        parameter: state.pages.newsList.getNewsPagesParameters[getCurrentPage(state).nodeId] ?? {
            parentNodeId: getCurrentPage(state).nodeId,
        },
    };
};

const mapDispatchToProps = {
    loadPagesForParent,
    updateLoadParameter,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface NewsListViewStyles {
    titleText?: TypographyProps;
    authorText?: TypographyProps;
    publishDateText?: TypographyProps;
    listWrapper?: InjectableCss;
    newsWrapper?: InjectableCss;
    summaryWrapper?: InjectableCss;
    dateAndAuthorWrapper?: InjectableCss;
    readMoreLink?: LinkPresentationProps;
    pagination?: PaginationPresentationProps;
    categoryGridItem?: GridItemProps;
    categoryHeading: InjectableCss;
    filterDropdown: InjectableCss;
    categoryText: InjectableCss;
    typeText: InjectableCss;
    categoryWrapper: InjectableCss;
    filterButton: GridItemProps;
}

export const newsListViewStyles: NewsListViewStyles = {
    titleText: {
        css: css`
            margin-bottom: 15px;
            font-size: 24px;
            padding: 0 10px;
        `,
    },
    newsWrapper: {
        css: css`
            position: relative;
            border: 1px solid #e4e4ed;
            box-shadow: 0 2px 4px #e4e4ed;
            width: 30%;
            margin: 15px;
            display: flex;
            flex-direction: column;
            background-color: #fff;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css``,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    pagination: {
        cssOverrides: {
            pagination: css`
                border-top: 1px solid ${getColor("common.border")};
                padding-top: 15px;
            `,
        },
    },
    summaryWrapper: {
        css: css`
            display: -webkit-box;
            display: -moz-box;
            display: -ms-flexbox;
            display: -webkit-flex;
            display: flex;
            -webkit-box-orient: vertical;
            -moz-box-orient: vertical;
            -webkit-flex-direction: column;
            -ms-flex-direction: column;
            flex-direction: column;
            /* optional */
            -webkit-box-align: start;
            -moz-box-align: start;
            -ms-flex-align: start;
            -webkit-align-items: flex-start;
            align-items: flex-start;

            p {
                &:nth-child(2) {
                    padding: 0 10px;
                    -webkit-box-ordinal-group: 3;
                    -moz-box-ordinal-group: 3;
                    -ms-flex-order: 3;
                    -webkit-order: 3;
                    order: 3;
                }
            }
        `,
    },
    authorText: {
        css: css`
            margin-right: 8px;
            color: #008061;
            font-weight: bold;
            .sub-title {
                color: rgb(102, 102, 102);
                font-weight: normal;
            }
        `,
    },
    publishDateText: {
        css: css`
            font-style: italic;
            color: rgb(102, 102, 102);
        `,
    },
    listWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            background-color: #f3f3f3;
            > div:first-child {
                position: relative;
                border: 1px solid #e4e4ed;
                box-shadow: 0 2px 4px #e4e4ed;
                width: 30%;
                margin: 15px;
                display: flex;
                flex-direction: column;
                background-color: #fff;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                width: 100%;
                            `,
                            css``,
                            null,
                        ],
                        "max",
                    )}
            }
        `,
    },
    dateAndAuthorWrapper: {
        css: css`
            margin-bottom: 10px;
            padding: 0 10px;
        `,
    },
    readMoreLink: {
        css: css`
            padding: 0 10px;
            margin-bottom: 10px;
        `,
    },
    categoryGridItem: {
        width: [12, 12, 12, 9, 9],
        css: css`
            > div {
                width: 100%;
                height: auto;
                display: block;
                padding: 20px 0;
                height: fit-content;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 15px !important;
                            display: flex;
                            flex-direction: column;
                        `,
                        css`
                            margin-bottom: 15px !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    categoryHeading: {
        css: css`
            h2 {
                margin-bottom: 15px;
                font-size: 25px;
                font-family: Helvetica-Bold;
                color: #474747;
                font-weight: bold;
            }
        `,
    },
    filterDropdown: {
        css: css`
            margin-right: 15px;
        `,
    },
    categoryText: {
        css: css`
            margin-top: 10px;
            font-weight: normal;
            font-size: 16px;
            color: rgb(102, 102, 102);
            margin-right: 5px;
        `,
    },
    typeText: {
        css: css`
            margin-top: 10px;
            font-weight: normal;
            color: rgb(102, 102, 102);
            font-size: 16px;
        `,
    },
    categoryWrapper: {
        css: css`
            margin-bottom: 10px;
            padding: 0 10px;
        `,
    },
    filterButton: {
        css: css`
            position: relative;
            top: 52px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            position: relative;
                            top: 0px;
                            text-align: center;
                        `,
                        css`
                            margin-bottom: 15px !important;
                        `,
                        null,
                    ],
                    "max",
                )}
        `,
    },
};

const styles = newsListViewStyles;

interface State {
    category: boolean;
    categoryData: Array<any>;
    typeData: Array<any>;
    categoryName: string;
    typeName: string;
    windowUrl: string;
    dataFiltered: boolean;
    dataPerPage: Array<any>;
    pageSize: number;
    page: number;
    isDataFiltered: boolean;
    totalItems: number;
    isSearchFilerApplied: boolean;
    isUrlRendering: boolean;
    isFilterRendering: boolean;
    isNewsPage: boolean;
}

class NewsListView extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            category: false,
            categoryData: [],
            typeData: [],
            categoryName: "",
            typeName: "",
            windowUrl: "",
            dataFiltered: true,
            dataPerPage: [],
            pageSize: props.fields.defaultPageSize,
            page: 1,
            isDataFiltered: false,
            totalItems: 0,
            isSearchFilerApplied: false,
            isUrlRendering: false,
            isFilterRendering: false,
            isNewsPage: false,
        };
    }
    UNSAFE_componentWillMount() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageSize = this.state.pageSize;
            window.localStorage.BlogListPageNumber = this.state.page;
            window.localStorage.SearchedDataStore = [];
        }
        this.categorySortOnUrl();
        if (!this.state.isDataFiltered) {
            this.randomUrlHandler();
        }
    }

    componentDidUpdate() {
        if (!this.props.pages.value && !this.props.pages.isLoading) {
            this.props.loadPagesForParent(this.props.parameter);
        }
        this.categorySortOnUrl();
        if (this.props.pages.value && this.state.categoryData.length === 0) {
            this.randomUrlHandler();
        }
    }

    componentDidMount() {
        if (window.parent.location.href.indexOf("type=") !== -1) {
            const typeSelected = window.parent.location.href.split("type=")[1].trim();
            const updatedUrl = `${window.location.href}?type=${typeSelected}`;
            this.setState({ windowUrl: updatedUrl });
        } else if (window.parent.location.href.indexOf("category=") !== -1) {
            const categorySelected = window.parent.location.href.split("category=")[1].trim();
            const updatedUrl = `${window.location.href}?category=${categorySelected}`;
            this.setState({ windowUrl: updatedUrl });
            this.setState({ isNewsPage: true });
        } else {
            this.setState({ windowUrl: window.location.href });
        }
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageSize = this.state.pageSize;
            window.localStorage.BlogListPageNumber = this.state.page;
            window.localStorage.SearchedDataStore = [];
        }

        this.categorySortOnUrl();
        if (!this.state.isDataFiltered) {
            this.randomUrlHandler();
        }
    }

    changePage = (newPageIndex: number) => {
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageNumber = newPageIndex;
        }

        if (this.state.isUrlRendering) {
            if (this.state.isDataFiltered) {
                if (this.state.windowUrl && this.state.windowUrl.indexOf("category=") !== -1) {
                    this.typeSort();
                }
                if (this.state.windowUrl && this.state.windowUrl.indexOf("type=") !== -1) {
                    this.categorySort();
                }
            }
            if (!this.state.isDataFiltered) {
                this.randomUrlHandler();
            }
        } else if (this.state.isFilterRendering) {
            if (typeof window !== "undefined") {
                const searchedDataStore = JSON.parse(localStorage.getItem("SearchedDataStore") || "[]");
                this.paginationConfiguration(searchedDataStore);
            }
        }
    };

    changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = event.currentTarget.value;
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageSize = newPageSize;
            window.localStorage.BlogListPageNumber = this.state.page;
        }

        if (this.state.isUrlRendering) {
            if (this.state.isDataFiltered) {
                if (this.state.windowUrl && this.state.windowUrl.indexOf("category=") !== -1) {
                    this.typeSort();
                }
                if (this.state.windowUrl && this.state.windowUrl.indexOf("type=") !== -1) {
                    this.categorySort();
                }
            } else {
                this.randomUrlHandler();
            }
        } else if (this.state.isFilterRendering) {
            if (typeof window !== "undefined") {
                const searchedDataStore = JSON.parse(localStorage.getItem("SearchedDataStore") || "[]");
                this.paginationConfiguration(searchedDataStore);
            }
        }
    };

    categorySortOnUrl = () => {
        let typeVal = "";

        if (
            (this.state.dataFiltered && this.state.windowUrl && this.state.windowUrl.indexOf("type=") !== -1) ||
            this.state.windowUrl.indexOf("category=") !== -1
        ) {
            if (this.state.dataFiltered && this.state.windowUrl && this.state.windowUrl.indexOf("type=") !== -1) {
                this.setState({ category: true });
                this.setState({ dataFiltered: false });
                let dynamicType = this.state.windowUrl.split("type=")[1].trim();
                dynamicType = decodeURI(dynamicType);
                this.setState({ typeName: dynamicType });
                typeVal = dynamicType;

                if (this.props.pages.value) {
                    const pages = this.props.pages.value;
                    const data = pages.filter(function (elem, pos) {
                        if (elem.fields.type === dynamicType) {
                            return elem;
                        }
                    });

                    this.setState({ isUrlRendering: true, isFilterRendering: false });
                    this.setState({ isDataFiltered: true });
                    this.paginationConfiguration(data);
                }
            }
            if (this.state.dataFiltered && this.state.windowUrl && this.state.windowUrl.indexOf("category=") !== -1) {
                this.setState({ category: true });
                this.setState({ dataFiltered: false });
                let dynamicCategory = this.state.windowUrl.split("category=")[1].trim();
                dynamicCategory = decodeURI(dynamicCategory);
                this.setState({ categoryName: dynamicCategory });
                if (this.props.pages.value) {
                    const pages = this.props.pages.value;
                    const data = pages.filter(function (elem, pos) {
                        if (elem.fields.category === dynamicCategory) {
                            return elem;
                        }
                    });

                    this.setState({ isUrlRendering: true, isFilterRendering: false });
                    this.setState({ isDataFiltered: true });
                    this.paginationConfiguration(data);
                }
            }
        }
    };

    categorySort = () => {
        let typeVal = "";
        if (this.state.windowUrl && this.state.windowUrl.indexOf("type=") !== -1) {
            this.setState({ category: true });
            this.setState({ dataFiltered: false });
            let dynamicType = this.state.windowUrl.split("type=")[1].trim();
            dynamicType = decodeURI(dynamicType);
            this.setState({ typeName: dynamicType });
            typeVal = dynamicType;

            if (this.props.pages.value) {
                const pages = this.props.pages.value;
                const data = pages.filter(function (elem, pos) {
                    if (elem.fields.type === dynamicType) {
                        return elem;
                    }
                });

                this.setState({ isUrlRendering: true, isFilterRendering: false });
                this.setState({ isDataFiltered: true });
                this.paginationConfiguration(data);
            }
        }
    };

    typeSort = () => {
        if (this.state.windowUrl && this.state.windowUrl.indexOf("category=") !== -1) {
            this.setState({ category: true });
            this.setState({ dataFiltered: false });
            let dynamicCategory = this.state.windowUrl.split("category=")[1].trim();
            dynamicCategory = decodeURI(dynamicCategory);
            this.setState({ categoryName: dynamicCategory });
            if (this.props.pages.value) {
                const pages = this.props.pages.value;
                const data = pages.filter(function (elem, pos) {
                    if (elem.fields.category === dynamicCategory) {
                        return elem;
                    }
                });

                this.setState({ isUrlRendering: true, isFilterRendering: false });
                this.setState({ isDataFiltered: true });
                this.paginationConfiguration(data);
            }
        }
    };

    randomUrlHandler = () => {
        const pages = this.props.pages.value;
        if (pages) {
            this.setState({ isUrlRendering: true, isFilterRendering: false });
            this.setState({ isDataFiltered: false });
            this.paginationConfiguration(pages);
        }
    };

    paginationConfiguration = (data: any) => {
        let updatedPageSize = 0;
        let updatedPageNumber = 0;

        if (typeof window !== "undefined") {
            window.localStorage.setItem("SearchedDataStore", JSON.stringify(data));
        }
        if (typeof window !== "undefined" && window.localStorage.BlogListPageSize) {
            updatedPageSize = window.localStorage.BlogListPageSize;
        }

        if (typeof window !== "undefined" && window.localStorage.BlogListPageNumber) {
            updatedPageNumber = window.localStorage.BlogListPageNumber;
        }

        if (this.state.categoryName) {
            let categoryWiseData: PageProps[] = [];

            const self = this;
            categoryWiseData = data.filter(function (elem: { fields: { category: string } }) {
                if (elem.fields.category === self.state.categoryName) {
                    return elem;
                }
            });
            const sortedAndPagedPages = sortBy(categoryWiseData, o => o.fields["publishDate"])
                .reverse()
                .slice((updatedPageNumber - 1) * updatedPageSize, updatedPageNumber * updatedPageSize);
            this.setState({ categoryData: sortedAndPagedPages, totalItems: categoryWiseData.length });
        }
        else if (this.state.typeName) {
            let typeWiseData: PageProps[] = [];
            
            const self = this;
            typeWiseData = data.filter(function (elem: { fields: { type: string } }) {
                if (elem.fields.type === self.state.typeName) {
                    return elem;
                }
            });
            // console.log('this.state.typeName',this.state.typeName)
            const newSortedAndPagedPages = sortBy(typeWiseData, o => o.fields["publishDate"])
                .reverse()
                .slice((updatedPageNumber - 1) * updatedPageSize, updatedPageNumber * updatedPageSize);
            this.setState({ categoryData: newSortedAndPagedPages, totalItems: typeWiseData.length });
        }
         else {
            const sortedAndPagedPages = sortBy(data, o => o.fields["publishDate"])
                .reverse()
                .slice((updatedPageNumber - 1) * updatedPageSize, updatedPageNumber * updatedPageSize);
            this.setState({ categoryData: sortedAndPagedPages, totalItems: data.length });
        }
    };

    CategoryDataHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ isUrlRendering: false, isFilterRendering: true });
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageSize = this.state.pageSize;
            window.localStorage.BlogListPageNumber = this.state.page;
        }
        const cat = event.target.value;
        this.setState({ category: true });
        this.setState({ categoryName: cat });
        let categoryWiseData: PageProps[] = [];
        const typeName = this.state.typeName;

        // eslint-disable-next-line array-callback-return
        if (this.state.windowUrl.indexOf("category=") === -1) {
            const url = `${this.state.windowUrl}?category=${cat}`;
            this.setState({ windowUrl: url });
        } else {
            const catUrl = this.state.windowUrl.split("?category=")[0];
            const updatedCatUrl = `${catUrl}?category=${cat}`;
            this.setState({ windowUrl: updatedCatUrl });
        }

        if (this.props.pages.value) {
            categoryWiseData = this.props.pages.value.filter(function (elem, pos) {
                if (typeName === "") {
                    if (elem.fields.category === cat) {
                        return elem;
                    }
                } else if (elem.fields.category === cat && elem.fields.type === typeName) {
                    return elem;
                }
            });
        }
        this.setState({ isDataFiltered: false });
        this.setState({ isSearchFilerApplied: true });
        this.paginationConfiguration(categoryWiseData);
    };

    TypeDataHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        this.setState({ isUrlRendering: false, isFilterRendering: true });
        if (typeof window !== "undefined") {
            window.localStorage.BlogListPageSize = this.state.pageSize;
            window.localStorage.BlogListPageNumber = this.state.page;
        }
        const typeValue = event.target.value;
        let categoryWiseData: PageProps[] = [];
        this.setState({ category: true });
        this.setState({ typeName: typeValue });
        const catName = this.state.categoryName;
        // eslint-disable-next-line array-callback-return
        if (this.props.pages.value) {
            categoryWiseData = this.props.pages.value.filter(function (elem, pos) {
                if (catName === "") {
                    if (elem.fields.type === typeValue) {
                        return elem;
                    }
                } else if (elem.fields.type === typeValue && elem.fields.category === catName) {
                    return elem;
                }
            });
        }
        this.setState({ isDataFiltered: false });
        this.setState({ isSearchFilerApplied: true });
        this.paginationConfiguration(categoryWiseData);
    };

    ClearFiterHandler = () => {
        this.setState({ isDataFiltered: false });

        const self = this;
        this.setState({ categoryName: "" }, function () {
            if (typeof window !== "undefined") {
                window.localStorage.BlogListPageSize = self.state.pageSize;
                window.localStorage.BlogListPageNumber = self.state.page;
            }
            if (self.props.pages.value) {
                self.paginationConfiguration(self.props.pages.value);
            }
        });
        this.setState({ typeName: "" });
    };

    render() {
        const {
            links,
            pages: { value: pages },
            fields: { defaultPageSize },
            parameter,
        } = this.props;

        if (!pages) {
            return null;
        }

        const defaultPageRange = [defaultPageSize, defaultPageSize * 2, defaultPageSize * 3, defaultPageSize * 4];

        const categories = pages.map(cat => cat.fields.category);
        const categoryList = categories.filter(function (elem, pos) {
            // eslint-disable-next-line eqeqeq
            return categories.indexOf(elem) == pos;
        });
        const type = pages.map(type => type.fields.type);
        const typeList = type.filter(function (elem, pos1) {
            // eslint-disable-next-line eqeqeq
            return type.indexOf(elem) == pos1;
        });

        const hideFilter = !!(this.state.windowUrl && this.state.windowUrl.indexOf("hidefilter=true") !== -1);
        if (hideFilter) {
            const body = document.body as HTMLElement;
            body.style.overflow = "hidden";
        }

        return (
            <>
                {hideFilter ? null : (
                    <GridItem {...styles.categoryGridItem}>
                        <StyledWrapper>
                            <StyledWrapper {...styles.categoryHeading}>
                                <Typography variant="h2">Filter By Category</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.filterDropdown}>
                                <Select onChange={e => this.CategoryDataHandler(e)} value={this.state.categoryName}>
                                    <option value="" disabled>
                                        {translate("Select Category")}
                                    </option>
                                    {categoryList
                                        .filter(category => category !== "")
                                        .map((category, i) => (
                                            <option key={i} value={category}>
                                                {category}
                                            </option>
                                        ))}
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper>
                            <StyledWrapper {...styles.categoryHeading}>
                                <Typography variant="h2">Filter By Type</Typography>
                            </StyledWrapper>
                            <StyledWrapper {...styles.filterDropdown}>
                                <Select onChange={e => this.TypeDataHandler(e)} value={this.state.typeName}>
                                    <option value="" disabled>
                                        {translate("Select Type")}
                                    </option>
                                    {typeList
                                        .filter(type => type !== "")
                                        .map((type, i) => (
                                            <option key={i} value={type}>
                                                {type}
                                            </option>
                                        ))}
                                </Select>
                            </StyledWrapper>
                        </StyledWrapper>
                        <StyledWrapper {...styles.filterButton}>
                            <Button onClick={this.ClearFiterHandler} data-test-selector="clear_filter_blogs">
                                {translate("CLEAR FILTERS")}
                            </Button>
                        </StyledWrapper>
                    </GridItem>
                )}
                {this.state.categoryData.length !== 0 && (
                    <StyledWrapper {...styles.listWrapper}>
                        {this.state.categoryData.map(page => (
                            <StyledWrapper {...styles.newsWrapper} key={page.id}>
                                {(page.fields["newsSummary"] || page.fields["newsContent"]) && (
                                    <StyledWrapper {...styles.summaryWrapper}>
                                        {parse(page.fields["newsSummary"] || page.fields["newsContent"], parserOptions)}
                                        <Typography variant="h2" {...styles.titleText}>
                                            <Link
                                                target="_blank"
                                                href={links[page.nodeId]?.url}
                                                data-test-selector="newslist_pagetitle"
                                            >
                                                {page.fields["title"]}
                                            </Link>
                                        </Typography>
                                        {/* <StyledWrapper {...styles.dateAndAuthorWrapper}>
                                            {page.fields["publishDate"] && (
                                                <Typography {...styles.publishDateText} data-test-selector="newslist_publishdate">
                                                    <LocalizedDateTime
                                                        dateTime={new Date(page.fields["publishDate"])}
                                                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                                                    />
                                                </Typography>
                                            )}
                                        </StyledWrapper> */}
                                        <StyledWrapper {...styles.categoryWrapper}>
                                            {page.fields["category"] && (
                                                <Typography {...styles.categoryText}>
                                                    {page.fields["category"]} |
                                                </Typography>
                                            )}
                                            {page.fields["type"] && (
                                                <Typography {...styles.typeText}>{page.fields["type"]}</Typography>
                                            )}
                                        </StyledWrapper>
                                    </StyledWrapper>
                                )}
                                <Link
                                    target="_blank"
                                    href={links[page.nodeId]?.url}
                                    {...styles.readMoreLink}
                                    data-test-selector={`newsList_ReadMore_${page.fields["title"]}`}
                                >
                                    {translate("Read More")}
                                </Link>
                            </StyledWrapper>
                        ))}

                        <StyledWrapper>
                            {typeof window !== "undefined" &&
                                this.state.categoryData &&
                                this.state.totalItems > window.localStorage.BlogListPageSize && (
                                    <Pagination
                                        resultsCount={this.state.totalItems}
                                        currentPage={window.localStorage.BlogListPageNumber}
                                        resultsPerPage={window.localStorage.BlogListPageSize}
                                        resultsPerPageOptions={defaultPageRange}
                                        onChangePage={this.changePage}
                                        onChangeResultsPerPage={this.changeResultsPerPage}
                                        pageSizeCookie="NewsList-PageSize"
                                        {...styles.pagination}
                                    />
                                )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </>
        );
    }
}

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(NewsListView)),
    definition: {
        group: "News List",
        allowedContexts: ["NewsListPage"],
        fieldDefinitions: [
            {
                name: fields.defaultPageSize,
                editorTemplate: "IntegerField",
                defaultValue: 5,
                fieldType: "General",
                min: 1,
                max: 20,
            },
        ],
    },
};

export default widgetModule;
